import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import type { ApprovalMode } from "../clientTools/approvalModes";

import { getClientToolPreferencesQueryKey } from "./useClientToolPreferences";

/** Maps tool spec name (e.g. "navigate", "create_events") to user's preferred approval mode. */
type ClientToolPreferences = Record<string, ApprovalMode>;

/**
 * Mutation hook for updating client-tool approval preferences.
 *
 * Uses optimistic updates with rollback on error, `onSettled`-based
 * invalidation (always refetches to get accurate server state), and
 * a mutation scope to serialize rapid successive updates.
 */
export function useUpdateClientToolPreference() {
  const queryClient = useQueryClient();
  const { configService } = useDomainServices();
  const queryKey = getClientToolPreferencesQueryKey();

  const mutation = useMutation({
    mutationFn: async (updated: ClientToolPreferences) => {
      await configService.setUserConfig("client_tool_preferences", updated);
      return updated;
    },
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<ClientToolPreferences>(queryKey);
      queryClient.setQueryData(queryKey, updated);
      return { previous };
    },
    onError: (error, _updated, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      ErrorMonitoringService.captureError(error);
    },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey });
    },
  });

  const updatePreference = (toolName: string, mode: ApprovalMode) => {
    const current =
      queryClient.getQueryData<ClientToolPreferences>(queryKey) ?? {};
    const updated = { ...current, [toolName]: mode };
    mutation.mutate(updated);
  };

  const resetPreferences = () => {
    mutation.mutate({});
  };

  return {
    updatePreference,
    resetPreferences,
    isSaving: mutation.isPending,
  };
}
